/**
 * A metadata value that can be copied to clipboard.
 * Compare with frontends/app/src/components/CopyableText.tsx
 */

import {WBIcon} from '@wandb/ui';
import {toast} from '@wandb/weave/common/components/elements/Toast';
import copyToClipboard from 'copy-to-clipboard';
import React, {useCallback} from 'react';

import {Analytics} from '../../services/analytics';
import {useSearchableJSONTreePropsContext} from './SearchableJSONTreeProvider';

type CopyableTextProps = {
  toastText?: string;
  rootLocation: string;
};

type TextProp = {
  children: string;
  copyText?: string;
};

type JSXElementProp = {
  children: JSX.Element;
  copyText: string;
};

export const CopyableText = ({
  children,
  copyText,
  toastText,
  rootLocation,
}: CopyableTextProps & (TextProp | JSXElementProp)) => {
  const {eventData} = useSearchableJSONTreePropsContext();
  const copy = useCallback(() => {
    if (copyText != null) {
      copyToClipboard(copyText);
    } else if (typeof children === 'string') {
      copyToClipboard(children);
    } else {
      console.error(
        'CopyableText: copyText is required when content is not a string'
      );
    }
    toast(toastText ?? 'Copied to clipboard');
    Analytics.track(`copy to clipboard from run overview`, {
      ...eventData,
      location: rootLocation,
    });
  }, [children, copyText, toastText, eventData, rootLocation]);

  const onClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    copy();
  };

  return (
    <div className="group inline">
      <div
        className="inline cursor-pointer rounded px-4 py-4 group-hover:bg-moon-100"
        onClick={onClick}>
        {children}
        <WBIcon
          className="mx-4 inline text-moon-500 opacity-0 group-hover:opacity-100"
          name="copy"
          size="small"
          onClick={onClick}
        />
      </div>
    </div>
  );
};

export default CopyableText;
